<template>
  <div
    class="viq-conv-item"
    :class="{ 'viq-conv-item--active': active }"
    @click="$emit('select')"
    @contextmenu.prevent="showContextMenu"
  >
    <div v-if="!editing" class="viq-conv-item__content">
      <div class="viq-conv-item__text">
        <span class="viq-conv-item__title" :title="conversation.title">
          {{ conversation.title || 'Untitled' }}
        </span>
        <span class="viq-conv-item__time">{{ timeAgo }}</span>
      </div>
    </div>

    <div v-else class="viq-conv-item__edit" @click.stop>
      <input
        ref="renameInput"
        v-model="renameValue"
        class="viq-conv-item__edit-input"
        @keyup.enter="confirmRename"
        @keyup.escape="cancelRename"
        @blur="confirmRename"
      />
    </div>

    <div v-if="!editing" class="viq-conv-item__actions" @click.stop>
      <button class="viq-conv-item__action" title="Rename" @click="startRename">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M8.5 1.5l2 2-7 7H1.5V8.5l7-7z" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
      <button class="viq-conv-item__action viq-conv-item__action--delete" title="Delete" @click="$emit('delete')">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M1.5 3h9M4.5 3V1.5h3V3M9.5 3v7.5h-7V3" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViqConversationItem",
  props: {
    conversation: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
      renameValue: "",
    };
  },
  computed: {
    conversationType() {
      return this.conversation.type || "ask";
    },
    typeLabel() {
      const labels = {
        ask: "Q&A",
        report: "Board Report",
        "board-report": "Board Report",
        timeline: "Timeline",
        "gap-analysis": "Gap Analysis",
        "market-entry": "Market Entry",
        "compare-markets": "Comparison",
        expansion: "Expansion",
      };
      return labels[this.conversationType] || "Q&A";
    },
    timeAgo() {
      const raw = this.conversation.updated_at || this.conversation.created_at;
      if (!raw) return "";
      // Handle unix timestamps (seconds) vs ISO strings
      const date = typeof raw === "number" || /^\d+$/.test(raw)
        ? new Date(Number(raw) * 1000)
        : new Date(raw);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    },
  },
  methods: {
    startRename() {
      this.renameValue = this.conversation.title || "";
      this.editing = true;
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus();
          this.$refs.renameInput.select();
        }
      });
    },
    confirmRename() {
      if (this.editing && this.renameValue.trim()) {
        this.$emit("rename", this.renameValue.trim());
      }
      this.editing = false;
    },
    cancelRename() {
      this.editing = false;
    },
    showContextMenu() {
      this.$emit("delete");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-conv-item {
  display: flex;
  align-items: flex-start;
  padding: $viq-space-sm 10px;
  border-radius: $viq-radius-md;
  cursor: pointer;
  transition: background $viq-transition-fast;
  position: relative;
  min-height: 48px;

  &:hover {
    background: $viq-bg-secondary;

    .viq-conv-item__actions {
      opacity: 1;
    }
  }

  &--active {
    background: $viq-accent-light;
  }

  &__content {
    display: flex;
    align-items: flex-start;
    gap: $viq-space-sm;
    flex: 1;
    min-width: 0;
  }

  &__badge {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: $viq-radius-full;
    white-space: nowrap;
    margin-top: 2px;

    &--ask {
      background: $viq-pill-qa-bg;
      color: $viq-pill-qa-text;
    }
    &--report,
    &--board-report {
      background: $viq-pill-report-bg;
      color: $viq-pill-report-text;
    }
    &--timeline {
      background: $viq-pill-timeline-bg;
      color: $viq-pill-timeline-text;
    }
    &--gap-analysis {
      background: $viq-pill-gap-bg;
      color: $viq-pill-gap-text;
    }
    &--market-entry,
    &--compare-markets,
    &--expansion {
      background: $viq-pill-qa-bg;
      color: $viq-pill-qa-text;
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: $viq-font-sm;
    color: $viq-text-primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }

  &__time {
    font-size: 10px;
    color: $viq-text-muted;
    line-height: 1.3;
  }

  &__actions {
    display: flex;
    gap: 2px;
    opacity: 0;
    transition: opacity $viq-transition-fast;
    margin-left: $viq-space-xs;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: none;
    color: $viq-text-muted;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    transition: background $viq-transition-fast, color $viq-transition-fast;

    &:hover {
      background: $viq-border;
      color: $viq-text-primary;
    }

    &--delete:hover {
      background: rgba(239, 68, 68, 0.1);
      color: $viq-error;
    }
  }

  &__edit {
    flex: 1;
  }

  &__edit-input {
    width: 100%;
    background: $viq-bg-primary;
    border: 1px solid $viq-accent;
    border-radius: $viq-radius-sm;
    color: $viq-text-primary;
    font-size: $viq-font-sm;
    padding: 4px 8px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 2px $viq-accent-light;
    }
  }
}
</style>
